import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Users, Plus, Copy, Check, LogOut, Crown, UserPlus, Link2 } from "lucide-react";

export const GroupsPanel = ({ isOpen, onClose, onSelectGroup }) => {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [joinCode, setJoinCode] = useState("");

  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${API}/groups`);
      setGroups(res.data);
    } catch (error) {
      console.error("Failed to fetch groups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchGroups();
    }
  }, [isOpen]);

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Please enter a group name");
      return;
    }

    try {
      const res = await axios.post(`${API}/groups`, formData);
      const inviteCode = res.data.invite_code;
      toast.success(
        <div>
          <p className="font-semibold">Group created!</p>
          <p className="text-sm mt-1">Invite code: <span className="font-mono font-bold">{inviteCode}</span></p>
        </div>,
        { duration: 8000 }
      );
      setGroups([res.data, ...groups]);
      setFormData({ name: "", description: "" });
      setShowCreateForm(false);
    } catch (error) {
      toast.error("Failed to create group");
    }
  };

  const handleJoinGroup = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) {
      toast.error("Please enter an invite code");
      return;
    }

    try {
      const res = await axios.post(`${API}/groups/join`, { invite_code: joinCode.trim() });
      toast.success(`Joined ${res.data.name}!`);
      setGroups([res.data, ...groups.filter(g => g.id !== res.data.id)]);
      setJoinCode("");
      setShowJoinForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join group");
    }
  };

  const handleLeaveGroup = async (groupId) => {
    if (!confirm("Are you sure you want to leave this group?")) return;
    
    try {
      await axios.post(`${API}/groups/${groupId}/leave`);
      toast.success("Left the group");
      setGroups(groups.filter(g => g.id !== groupId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to leave group");
    }
  };

  const copyInviteCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      toast.success("Invite code copied!");
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card border-none rounded-2xl p-0 h-[85vh] sm:h-auto sm:max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="p-6 pb-4 border-b border-charcoal/5 shrink-0">
          <DialogTitle className="font-serif text-xl text-charcoal flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Groups
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Create or join groups for shared bucket lists with friends
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : showCreateForm ? (
              <form onSubmit={handleCreateGroup} className="space-y-4 p-4 bg-background rounded-xl border border-charcoal/10">
                <h3 className="font-medium text-charcoal">Create New Group</h3>
                <div className="space-y-2">
                  <Label className="text-charcoal">Group Name</Label>
                  <Input
                    placeholder="e.g., Adventure Squad"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-card border-charcoal/10 rounded-lg"
                    data-testid="group-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-charcoal">Description (optional)</Label>
                  <Textarea
                    placeholder="What's this group about?"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-card border-charcoal/10 rounded-lg resize-none"
                    rows={2}
                    data-testid="group-description-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)} className="flex-1 rounded-lg border-charcoal/10">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-primary text-white hover:bg-primary/90 rounded-lg" data-testid="create-group-btn">
                    Create Group
                  </Button>
                </div>
              </form>
            ) : showJoinForm ? (
              <form onSubmit={handleJoinGroup} className="space-y-4 p-4 bg-background rounded-xl border border-charcoal/10">
                <h3 className="font-medium text-charcoal">Join a Group</h3>
                <div className="space-y-2">
                  <Label className="text-charcoal">Invite Code</Label>
                  <Input
                    placeholder="Enter invite code..."
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                    className="bg-card border-charcoal/10 rounded-lg font-mono text-center tracking-widest"
                    maxLength={8}
                    data-testid="join-group-code-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowJoinForm(false)} className="flex-1 rounded-lg border-charcoal/10">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-primary text-white hover:bg-primary/90 rounded-lg" data-testid="join-group-btn">
                    Join Group
                  </Button>
                </div>
              </form>
            ) : groups.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="font-medium text-charcoal mb-2">No groups yet</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create a group or join one with an invite code
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {groups.map((group) => {
                  const isAdmin = group.members.find(m => m.id === user.id)?.role === "admin";
                  
                  return (
                    <div
                      key={group.id}
                      className="p-4 bg-background rounded-xl border border-charcoal/5"
                      data-testid={`group-${group.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-medium text-charcoal flex items-center gap-2">
                            {group.name}
                            {isAdmin && <Crown className="w-4 h-4 text-accent" />}
                          </h3>
                          {group.description && (
                            <p className="text-sm text-muted-foreground">{group.description}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex -space-x-2">
                          {group.members.slice(0, 4).map((member, idx) => (
                            <div
                              key={member.id}
                              className="w-8 h-8 rounded-full bg-primary/10 border-2 border-card flex items-center justify-center text-xs font-medium text-primary"
                              title={member.name}
                            >
                              {member.name[0].toUpperCase()}
                            </div>
                          ))}
                          {group.members.length > 4 && (
                            <div className="w-8 h-8 rounded-full bg-muted border-2 border-card flex items-center justify-center text-xs font-medium text-muted-foreground">
                              +{group.members.length - 4}
                            </div>
                          )}
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {group.members.length} member{group.members.length !== 1 ? "s" : ""}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 p-2 bg-card rounded-lg mb-3">
                        <Link2 className="w-4 h-4 text-muted-foreground" />
                        <code className="text-sm font-mono flex-1">{group.invite_code}</code>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => copyInviteCode(group.invite_code)}
                          className="h-7 w-7"
                        >
                          {copiedCode === group.invite_code ? (
                            <Check className="w-4 h-4 text-secondary" />
                          ) : (
                            <Copy className="w-4 h-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            onSelectGroup(group);
                            onClose();
                          }}
                          className="flex-1 rounded-lg border-charcoal/10"
                          data-testid={`view-group-${group.id}`}
                        >
                          View Items
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleLeaveGroup(group.id)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <LogOut className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </div>

        {!showCreateForm && !showJoinForm && (
          <div className="p-4 pb-6 border-t border-charcoal/5 flex gap-2 shrink-0">
            <Button
              onClick={() => setShowCreateForm(true)}
              className="flex-1 bg-primary text-white hover:bg-primary/90 rounded-xl h-11"
              data-testid="show-create-group-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Group
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowJoinForm(true)}
              className="flex-1 rounded-xl h-11 border-charcoal/10"
              data-testid="show-join-group-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Join Group
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GroupsPanel;
